// src/pages/Appli/FormationDirigeants.jsx
import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "./../../hooks/useScollToTop";
import Button from "../../components/Button/Button.jsx";
import {
  FaExternalLinkAlt,
  FaInfoCircle,
  FaChalkboardTeacher,
  FaCertificate,
  FaFilePowerpoint,
  FaPlayCircle,
  FaImages,
  FaSyncAlt,
} from "react-icons/fa";

import { getTrainingArticles } from "../../services/trainingArticleServices.js";

/* =========================
   Constantes / assets
   ========================= */

const HERO_URL =
  "/img/cover/cover_formation_dirigeants.jpg"; // ⚠️ remplace par une image formation
const BREADCRUMB_LABEL = "Formation des Dirigeants";

const CATEGORY_LABELS = {
  DIRIGEANTS: "Dirigeants",
  CERTIF_ASSO: "Certif Asso",
};

// Helpers media
const isProbablyHtml = (s = "") => /<[^>]+>/.test(String(s || ""));
const safeText = (s = "") => String(s || "").trim();

function youtubeEmbedUrl(url = "") {
  const u = String(url || "").trim();
  if (!u) return null;

  // youtu.be/<id>
  const short = u.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
  if (short?.[1]) return `https://www.youtube.com/embed/${short[1]}`;

  // youtube.com/watch?v=<id>
  const watch = u.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
  if (watch?.[1]) return `https://www.youtube.com/embed/${watch[1]}`;

  // youtube.com/embed/<id>
  const embed = u.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/);
  if (embed?.[1]) return `https://www.youtube.com/embed/${embed[1]}`;

  return null;
}

/* =========================
   Page
   ========================= */

export default function FormationDirigeants() {
  useScrollToTop();

  const [category, setCategory] = useState("DIRIGEANTS"); // DIRIGEANTS | CERTIF_ASSO
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setError("");
      setLoading(true);
      // published=1 pour le public
      const data = await getTrainingArticles({
        category,
        published: "1",
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les contenus pour le moment.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const count = rows.length;

  return (
    <div className="w-full pt-[70px] font-satoshi">
      <HeroImage />
      <HeaderBreadcrumb />

      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <QuickNav />

        <IntroSection
          category={category}
          setCategory={setCategory}
          onRefresh={load}
          loading={loading}
        />

        <ArticlesSection
          category={category}
          rows={rows}
          loading={loading}
          error={error}
        />

        <ContactSection />
      </div>
    </div>
  );
}

/* =========================
   Sous-composants (style InfoCN)
   ========================= */

function HeroImage() {
  return (
    <div className="relative h-[280px] md:h-[380px] w-full overflow-hidden">
      <img
        src={HERO_URL}
        alt="Formation des dirigeants — Ligue Nouvelle-Aquitaine Judo"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent" />
      <div className="relative z-10 max-w-[1200px] mx-auto h-full flex items-end px-4 pb-6">
        <div className="text-white drop-shadow">
          <h1 className="text-2xl md:text-4xl font-bold">
            Formation des Dirigeants
          </h1>
          <p className="text-sm md:text-base opacity-90 mt-1">
            Ressources • Guides • Certif Asso • Bonnes pratiques
          </p>
        </div>
      </div>
    </div>
  );
}

function HeaderBreadcrumb() {
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
      <div className="relative text-white px-4 py-4 md:py-5 max-w-[1200px] mx-auto">
        <p className="text-xs md:text-sm opacity-90">
          <a href="/" className="hover:underline">
            Accueil
          </a>{" "}
          &gt; {BREADCRUMB_LABEL}
        </p>
      </div>
    </div>
  );
}

function QuickNav() {
  return (
    <nav
      aria-label="Sommaire"
      className="mb-6 rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-800/70 backdrop-blur p-3"
    >
      <ul className="flex flex-wrap gap-2">
        <li>
          <AnchorChip href="#presentation" label="Présentation" />
        </li>
        <li>
          <AnchorChip href="#ressources" label="Ressources" />
        </li>
        <li>
          <AnchorChip href="#contact" label="Contact" />
        </li>
      </ul>
    </nav>
  );
}

function AnchorChip({ href, label }) {
  return (
    <a
      href={href}
      className="px-3 py-1.5 rounded-full text-sm bg-white dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 ring-1 ring-slate-200/70 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
    >
      {label}
    </a>
  );
}

function IntroSection({ category, setCategory, onRefresh, loading }) {
  return (
    <section
      id="presentation"
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6 mb-6"
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-semibold mb-2">
            Formation & Ressources pour la vie associative
          </h2>
          <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
            Cette page regroupe des <strong>articles</strong> (guides,
            procédures, documents) dédiés aux{" "}
            <strong>dirigeants de clubs</strong> ainsi qu’au parcours{" "}
            <strong>Certif Asso</strong>. Les contenus sont publiés par la
            commission formation de la Ligue.
          </p>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <InfoCard
              icon={
                <FaChalkboardTeacher className="text-slate-500 dark:text-slate-300" />
              }
              title="Dirigeants"
              items={[
                "Gestion administrative",
                "Bonnes pratiques associatives",
                "Outils & modèles",
                "Organisation et communication",
              ]}
            />
            <InfoCard
              icon={
                <FaCertificate className="text-slate-500 dark:text-slate-300" />
              }
              title="Certif Asso"
              items={[
                "Parcours & documents utiles",
                "Préparer les justificatifs",
                "Suivi et recommandations",
                "Ressources officielles",
              ]}
            />
          </div>
        </div>

        <div className="shrink-0 flex flex-col gap-2">
          <div className="inline-flex rounded-xl ring-1 ring-slate-200 overflow-hidden bg-white">
            <button
              type="button"
              onClick={() => setCategory("DIRIGEANTS")}
              className={[
                "px-4 py-2 text-sm font-semibold",
                category === "DIRIGEANTS"
                  ? "bg-indigo-600 text-white"
                  : "bg-white hover:bg-slate-50 text-slate-700",
              ].join(" ")}
              title="Voir les ressources Dirigeants"
            >
              Dirigeants
            </button>
            <button
              type="button"
              onClick={() => setCategory("CERTIF_ASSO")}
              className={[
                "px-4 py-2 text-sm font-semibold",
                category === "CERTIF_ASSO"
                  ? "bg-indigo-600 text-white"
                  : "bg-white hover:bg-slate-50 text-slate-700",
              ].join(" ")}
              title="Voir les ressources Certif Asso"
            >
              Certif Asso
            </button>
          </div>

          <Button
            text={loading ? "Chargement..." : "Rafraîchir"}
            variant="outline"
            size="sm"
            onClick={onRefresh}
            icon={FaSyncAlt}
          />
        </div>
      </div>
    </section>
  );
}

function InfoCard({ title, items = [], icon }) {
  return (
    <div className="rounded-xl p-4 ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50/70 dark:bg-slate-900/40">
      <div className="flex items-center gap-2 mb-2">
        {icon || (
          <FaInfoCircle className="text-slate-500 dark:text-slate-300" />
        )}
        <h3 className="font-medium">{title}</h3>
      </div>
      <ul className="text-sm text-slate-700 dark:text-slate-300 list-disc pl-5">
        {items.map((it) => (
          <li key={it} className="leading-6">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ArticlesSection({ category, rows, loading, error }) {
  const label = CATEGORY_LABELS[category] || category;

  return (
    <section
      id="ressources"
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6 mb-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl md:text-2xl font-semibold">
          Ressources — {label}
        </h2>
        <span className="text-sm px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
          {loading
            ? "..."
            : `${rows.length} article${rows.length > 1 ? "s" : ""}`}
        </span>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl p-4 bg-rose-50 text-rose-800 ring-1 ring-rose-200">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-xl p-5 ring-1 ring-dashed ring-slate-300 dark:ring-slate-700 bg-white/70 dark:bg-slate-800/60">
          <div className="font-semibold">Contenu en cours de rédaction</div>
          <div className="text-sm opacity-80 mt-1">
            Aucun article publié pour le moment dans cette catégorie.
          </div>
        </div>
      ) : (
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {rows.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      )}
    </section>
  );
}

function ArticleCard({ article }) {
  const photos = useMemo(() => {
    return [article.photo_url_1, article.photo_url_2, article.photo_url_3]
      .map((x) => safeText(x))
      .filter(Boolean);
  }, [article]);

  const video = safeText(article.video_url);
  const ppt = safeText(article.powerpoint_url);

  const yt = youtubeEmbedUrl(video);

  return (
    <article className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/30 overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg md:text-xl font-semibold leading-snug">
              {article.title || "Sans titre"}
            </h3>
            {article.subtitle ? (
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                {article.subtitle}
              </p>
            ) : null}
          </div>
        </div>

        {/* Contenu */}
        <div className="mt-4 text-sm md:text-[15px] text-slate-700 dark:text-slate-200 leading-relaxed">
          {isProbablyHtml(article.content) ? (
            <div
              className="prose prose-slate max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          ) : (
            <p className="whitespace-pre-wrap">{article.content}</p>
          )}
        </div>

        {/* Photos */}
        {photos.length > 0 ? (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-70">
              <FaImages />
              Photos
            </div>
            <div className="mt-2 grid gap-2 grid-cols-2 sm:grid-cols-3">
              {photos.map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() =>
                    window.open(src, "_blank", "noopener,noreferrer")
                  }
                  className="group rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-50 dark:bg-slate-900"
                  title="Ouvrir l'image"
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-28 sm:h-32 object-cover group-hover:scale-[1.02] transition"
                  />
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Vidéo */}
        {video ? (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-70">
              <FaPlayCircle />
              Vidéo
            </div>

            {yt ? (
              <div className="mt-2 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-black">
                <div
                  className="relative w-full"
                  style={{ paddingTop: "56.25%" }}
                >
                  <iframe
                    title={`video-${article.id}`}
                    src={yt}
                    className="absolute inset-0 w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <Button
                  text="Ouvrir la vidéo"
                  variant="primary"
                  size="sm"
                  onClick={() =>
                    window.open(video, "_blank", "noopener,noreferrer")
                  }
                  icon={FaExternalLinkAlt}
                />
              </div>
            )}
          </div>
        ) : null}

        {/* PowerPoint */}
        {ppt ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              text="Ouvrir le support (PowerPoint)"
              variant="outline"
              size="sm"
              onClick={() => window.open(ppt, "_blank", "noopener,noreferrer")}
              icon={FaFilePowerpoint}
            />
          </div>
        ) : null}
      </div>
    </article>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200/70 bg-white/80 overflow-hidden p-5">
      <div className="h-5 w-2/3 bg-slate-200/70 rounded" />
      <div className="mt-2 h-4 w-1/2 bg-slate-200/60 rounded" />
      <div className="mt-4 space-y-2">
        <div className="h-3 w-full bg-slate-200/60 rounded" />
        <div className="h-3 w-11/12 bg-slate-200/60 rounded" />
        <div className="h-3 w-10/12 bg-slate-200/60 rounded" />
      </div>
      <div className="mt-4 h-28 w-full bg-slate-200/50 rounded-xl" />
    </div>
  );
}

function ContactSection() {
  return (
    <section
      id="contact"
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6 mb-6"
    >
      <h2 className="text-xl md:text-2xl font-semibold">Contact</h2>
      <p className="text-slate-700 dark:text-slate-200 mt-2 leading-relaxed">
        Une question sur la formation des dirigeants ou le parcours Certif Asso
        ? Contactez la commission formation de la Ligue.
      </p>

      {/* ⚠️ remplace l’email si tu en as un */}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          text="Nous contacter"
          variant="primary"
          size="sm"
          onClick={() =>
            (window.location.href = "mailto:formation@votre-ligue.fr")
          }
          icon={FaExternalLinkAlt}
        />
      </div>
    </section>
  );
}
